import React, { useState, useEffect } from 'react';
import {
  AppBar, Toolbar, Typography, Box, IconButton, Drawer,
  List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  Divider, Chip, Avatar, Tooltip, useMediaQuery, useTheme, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert,
  CircularProgress, Menu, MenuItem, Badge, Snackbar, Popover,
} from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SearchIcon from '@mui/icons-material/Search';
import PeopleIcon from '@mui/icons-material/People';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import BarChartIcon from '@mui/icons-material/BarChart';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import GroupIcon from '@mui/icons-material/Group';
import SecurityIcon from '@mui/icons-material/Security';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import BadgeIcon from '@mui/icons-material/Badge';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import MedicationIcon from '@mui/icons-material/Medication';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import ScienceIcon from '@mui/icons-material/Science';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DomainIcon from '@mui/icons-material/Domain';
import { useContext } from 'react';
import { ColorModeContext } from '../App';
import { useAuth } from '../hooks/useAuth';
import { useClinicFeatures } from '../hooks/useClinicFeatures';
import { useNotifications } from '../hooks/useNotifications';
import { useNotification } from '../hooks/useNotification';
import { changePassword, getAppointments, getMyDoctorProfile, updateMyDoctorProfile, listAllClinics, switchToClinic, exitSupportSession } from '../services/api';
import GlobalSearch from './GlobalSearch';
import BrandMark from './BrandMark';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import { GuiaInstalacionDialog } from './InstalarApp';

const ChangePasswordDialog = ({ open, onClose }) => {
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [saving, setSaving] = useState(false);

  const set = (f) => (e) => setForm(p => ({ ...p, [f]: e.target.value }));

  const handleClose = () => {
    setForm({ current_password: '', new_password: '', confirm: '' });
    setError('');
    setSuccess(false);
    onClose();
  };

  const handleSubmit = async () => {
    if (form.new_password.length < 8) { setError('La nueva contraseña debe tener al menos 8 caracteres.'); return; }
    if (form.new_password !== form.confirm) { setError('Las contraseñas nuevas no coinciden.'); return; }
    setSaving(true);
    setError('');
    try {
      await changePassword({ current_password: form.current_password, new_password: form.new_password });
      setSuccess(true);
      // Cambiar la contraseña invalida el JWT actual en el backend (ver
      // auth.get_current_user) — dejar al usuario "adentro" con la sesión
      // vieja solo pospone un 401 sorpresivo en el próximo click, que
      // además caería en el mensaje genérico de "sesión expiró por
      // inactividad" (session_expired en Login.js), engañoso acá. Se
      // fuerza el logout acá mismo con un mensaje propio y correcto.
      setTimeout(() => {
        sessionStorage.setItem('password_changed', '1');
        localStorage.removeItem('token');
        window.location.href = '/login';
      }, 1500);
    } catch (err) {
      setError(err.userMessage || 'Error al cambiar la contraseña.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Cambiar contraseña</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">Contraseña actualizada correctamente.</Alert>}
        <TextField label="Contraseña actual" type="password" fullWidth value={form.current_password} onChange={set('current_password')} autoFocus
          error={!!error && !error.includes('nueva') && !error.includes('coinciden')} />
        <TextField label="Nueva contraseña" type="password" fullWidth value={form.new_password} onChange={set('new_password')}
          error={!!error && (error.includes('al menos 8') || error.includes('coinciden'))}
          helperText={error && (error.includes('al menos 8') || error.includes('coinciden')) ? error : 'Mínimo 8 caracteres'} />
        <TextField label="Confirmar nueva contraseña" type="password" fullWidth value={form.confirm} onChange={set('confirm')}
          error={!!error && error.includes('coinciden')} />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleSubmit}
          disabled={saving || !form.current_password || !form.new_password || !form.confirm}>
          {saving ? <CircularProgress size={18} /> : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DoctorProfileDialog = ({ open, onClose }) => {
  const [form, setForm] = useState({ phone: '', email: '', matricula: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getMyDoctorProfile()
      .then(r => setForm({ phone: r.data.phone || '', email: r.data.email || '', matricula: r.data.matricula || '' }))
      .catch(() => setError('No se pudo cargar el perfil'))
      .finally(() => setLoading(false));
  }, [open]);

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      await updateMyDoctorProfile({ phone: form.phone || null, email: form.email || null, matricula: form.matricula || null });
      onClose();
    } catch (err) { setError(err.userMessage || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Mi perfil médico</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
        {error && <Alert severity="error">{error}</Alert>}
        {loading ? <CircularProgress sx={{ alignSelf: 'center' }} /> : (
          <>
            <TextField label="Teléfono" fullWidth value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} />
            <TextField label="Email" fullWidth value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} />
            <TextField label="Matrícula" fullWidth value={form.matricula} onChange={e => setForm(p => ({ ...p, matricula: e.target.value }))} helperText="Necesaria para emitir recetas" />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || loading}>
          {saving ? <CircularProgress size={18} /> : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ROLE_LABELS = { admin: 'Admin', recepcionista: 'Recep.', medico: 'Médico', invitado: 'Invitado', superadmin: 'Superadmin' };
const ROLE_COLORS = { admin: 'error', recepcionista: 'primary', medico: 'success', invitado: 'default', superadmin: 'secondary' };

// Selector visible solo para un superadmin SIN clínica activa todavía
// ("modo plataforma") — elegir una clínica dispara el switch y recarga el
// usuario, que entra a esa clínica como si fuera su propio admin.
const ClinicPicker = () => {
  const { reload } = useAuth();
  const notify = useNotification();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(false);

  const open = (e) => {
    setAnchorEl(e.currentTarget);
    setLoading(true);
    listAllClinics().then(r => setClinics(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  };
  const close = () => setAnchorEl(null);

  const handlePick = async (clinicId) => {
    setSwitching(true);
    try {
      const res = await switchToClinic(clinicId);
      localStorage.setItem('token', res.data.access_token);
      await reload();
      close();
      // Tras el switch, role pasa de "superadmin" a "admin" — si estábamos
      // en /superadmin esa ruta ya no es accesible con el rol nuevo.
      navigate('/metrics', { replace: true });
    } catch (err) {
      notify(err.userMessage || 'No se pudo entrar a esa clínica', 'error');
    } finally {
      setSwitching(false);
    }
  };

  return (
    <>
      <Button
        onClick={open}
        startIcon={<DomainIcon sx={{ fontSize: '16px !important' }} />}
        size="small"
        sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.4)', fontSize: '0.78rem', mr: 1 }}
        variant="outlined"
      >
        Elegir clínica
      </Button>
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={close} PaperProps={{ sx: { maxHeight: 360, minWidth: 260 } }}>
        {loading ? (
          <MenuItem disabled><CircularProgress size={16} sx={{ mr: 1 }} />Cargando…</MenuItem>
        ) : clinics.length === 0 ? (
          <MenuItem disabled>Sin clínicas registradas</MenuItem>
        ) : clinics.map(c => (
          <MenuItem key={c.id} onClick={() => handlePick(c.id)} disabled={switching}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="body2" fontWeight={600}>{c.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {c.active_users} usuario{c.active_users === 1 ? '' : 's'} · {c.active_patients} paciente{c.active_patients === 1 ? '' : 's'}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

// Franja persistente mientras un superadmin está "actuando como" admin de
// una clínica ajena (ver routers/superadmin.py) — pedido explícito del
// diseño aprobado: nunca debe ser posible olvidar que se está operando
// sobre datos de otra clínica. Color distintivo (no usado por ningún
// estado de turno) para que no se confunda con el resto de la UI.
const SupportBanner = ({ clinicName }) => {
  const { reload } = useAuth();
  const notify = useNotification();
  const navigate = useNavigate();
  const [exiting, setExiting] = useState(false);

  const handleExit = async () => {
    setExiting(true);
    try {
      const res = await exitSupportSession();
      localStorage.setItem('token', res.data.access_token);
      await reload();
      // Al salir, clinic_id vuelve a null — cualquier ruta con
      // requireClinic=true (la mayoría) ya no es accesible con ese estado.
      navigate('/superadmin', { replace: true });
      notify('Volviste al modo plataforma', 'info');
    } catch (err) {
      notify(err.userMessage || 'No se pudo salir de la sesión de soporte', 'error');
    } finally {
      setExiting(false);
    }
  };

  return (
    <Box sx={{
      bgcolor: '#7a2e22', color: '#fde8e2',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5,
      px: 2, py: 0.75, fontSize: '0.82rem', flexWrap: 'wrap', textAlign: 'center',
    }}>
      <WarningAmberIcon sx={{ fontSize: 18 }} />
      <Typography sx={{ fontSize: 'inherit' }}>
        Operando como soporte en <strong>{clinicName || 'una clínica'}</strong>
      </Typography>
      <Button
        size="small"
        onClick={handleExit}
        disabled={exiting}
        sx={{
          color: '#fde8e2', textDecoration: 'underline', minWidth: 0, p: 0,
          fontSize: 'inherit', fontWeight: 600,
          '&:hover': { bgcolor: 'transparent', opacity: 0.8 },
        }}
      >
        {exiting ? 'Saliendo…' : 'Salir'}
      </Button>
    </Box>
  );
};

const todayBadge = (count) => (
  <Badge badgeContent={count || 0} color="primary" max={99}
    sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 14, minWidth: 14, top: -2, right: -4 } }}>
    <CalendarMonthIcon sx={{ fontSize: 18 }} />
  </Badge>
);

// Nav items per role. Items with drawerOnly:true appear only in the mobile drawer (+ account menu).
// Items with type:'section' render as a labelled divider in the drawer.
const buildNavItems = (isAdmin, isMedico, isRecepcionista, isInvitado, todayCount, features) => {
  // Invitado (acceso externo, solo Algoritmos publicados) — ni siquiera
  // depende de features?.algoritmos: si su cuenta existe, el admin ya
  // decidió darle ese acceso puntual.
  if (isInvitado) return [
    { label: 'Algoritmos', path: '/algoritmos', icon: <AccountTreeIcon sx={{ fontSize: 18 }} /> },
  ];


  if (isAdmin) return [
    { label: 'Dashboard', path: '/metrics', icon: <BarChartIcon sx={{ fontSize: 18 }} /> },
    { label: 'Agenda', path: '/agenda', icon: todayBadge(todayCount) },
    // Antes esta vista (sala de espera + check-in + agenda del día con
    // cambio de estado inline) le quedaba bloqueada a admin por
    // requiredRoles en App.js, sin ninguna equivalente en /metrics —
    // pedido explícito del usuario 2026-11-02: admin ya tiene superset de
    // permisos de recepcionista, necesita la misma visibilidad operativa.
    { label: 'Sala de espera', path: '/dashboard-recepcionista', icon: <HomeIcon sx={{ fontSize: 18 }} /> },
    { label: 'Turnos', path: '/turnos', icon: <SearchIcon sx={{ fontSize: 18 }} /> },
    { label: 'Pacientes', path: '/patients', icon: <PeopleIcon sx={{ fontSize: 18 }} /> },
    { label: 'Médicos', path: '/doctors', icon: <MedicalServicesIcon sx={{ fontSize: 18 }} /> },
    ...features?.billing ? [{ label: 'Facturación', path: '/facturacion', icon: <AttachMoneyIcon sx={{ fontSize: 18 }} /> }] : [],
    // Management items — visible only in mobile drawer and desktop account menu
    { type: 'section', label: 'Administración' },
    { label: 'Usuarios', path: '/users-admin', icon: <GroupIcon sx={{ fontSize: 18 }} />, drawerOnly: true },
    { label: 'Plantillas', path: '/templates', icon: <AssignmentIcon sx={{ fontSize: 18 }} />, drawerOnly: true },
    ...features?.prescriptions ? [{ label: 'Recetas', path: '/recetas', icon: <MedicationIcon sx={{ fontSize: 18 }} />, drawerOnly: true }] : [],
    ...features?.algoritmos ? [{ label: 'Algoritmos', path: '/algoritmos', icon: <AccountTreeIcon sx={{ fontSize: 18 }} />, drawerOnly: true }] : [],
    ...features?.dicom_studies ? [{ label: 'Estudios sin asignar', path: '/dicom-pendientes', icon: <ScienceIcon sx={{ fontSize: 18 }} />, drawerOnly: true }] : [],
    { label: 'Comunicaciones', path: '/comunicaciones', icon: <WhatsAppIcon sx={{ fontSize: 18 }} />, drawerOnly: true },
    { label: 'Auditoría', path: '/audit', icon: <SecurityIcon sx={{ fontSize: 18 }} />, drawerOnly: true },
    { label: 'Configuración', path: '/settings', icon: <SettingsIcon sx={{ fontSize: 18 }} />, drawerOnly: true },
  ];

  if (isRecepcionista) return [
    { label: 'Inicio', path: '/dashboard-recepcionista', icon: <HomeIcon sx={{ fontSize: 18 }} /> },
    { label: 'Agenda', path: '/agenda', icon: todayBadge(todayCount) },
    { label: 'Turnos', path: '/turnos', icon: <SearchIcon sx={{ fontSize: 18 }} /> },
    { label: 'Pacientes', path: '/patients', icon: <PeopleIcon sx={{ fontSize: 18 }} /> },
    ...features?.billing ? [{ label: 'Facturación', path: '/facturacion', icon: <AttachMoneyIcon sx={{ fontSize: 18 }} /> }] : [],
    { label: 'Métricas', path: '/metrics', icon: <BarChartIcon sx={{ fontSize: 18 }} /> },
    { label: 'Comunicaciones', path: '/comunicaciones', icon: <WhatsAppIcon sx={{ fontSize: 18 }} />, drawerOnly: true },
  ];

  if (isMedico) return [
    // Misma página que recepcionista/admin ahora (pedido explícito del
    // usuario 2026-11-02) — "Inicio", no "Mis pacientes", porque ya no es
    // una vista exclusiva del médico.
    { label: 'Inicio', path: '/dashboard-recepcionista', icon: <HomeIcon sx={{ fontSize: 18 }} /> },
    { label: 'Agenda', path: '/agenda', icon: todayBadge(todayCount) },
    { label: 'Turnos', path: '/turnos', icon: <SearchIcon sx={{ fontSize: 18 }} /> },
    { label: 'Pacientes', path: '/patients', icon: <PeopleIcon sx={{ fontSize: 18 }} /> },
    { label: 'Plantillas', path: '/templates', icon: <AssignmentIcon sx={{ fontSize: 18 }} /> },
    ...features?.prescriptions ? [{ label: 'Recetas', path: '/recetas', icon: <MedicationIcon sx={{ fontSize: 18 }} /> }] : [],
    ...features?.algoritmos ? [{ label: 'Algoritmos', path: '/algoritmos', icon: <AccountTreeIcon sx={{ fontSize: 18 }} /> }] : [],
    { label: 'Métricas', path: '/metrics', icon: <BarChartIcon sx={{ fontSize: 18 }} /> },
    ...features?.dicom_studies ? [{ label: 'Estudios sin asignar', path: '/dicom-pendientes', icon: <ScienceIcon sx={{ fontSize: 18 }} />, drawerOnly: true }] : [],
    { label: 'Comunicaciones', path: '/comunicaciones', icon: <WhatsAppIcon sx={{ fontSize: 18 }} />, drawerOnly: true },
  ];

  return [];
};

const Layout = ({ children }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [notifAnchor, setNotifAnchor] = useState(null);
  const [pwDialogOpen, setPwDialogOpen] = useState(false);
  const [doctorProfileOpen, setDoctorProfileOpen] = useState(false);
  const [instalarGuiaOpen, setInstalarGuiaOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [sessionWarning, setSessionWarning] = useState(false);
  const [todayCount, setTodayCount] = useState(0);
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const { isAdmin, isMedico, isRecepcionista, isInvitado, isSuperadmin, actingAsSupport, role, user, logout } = useAuth();
  const { features } = useClinicFeatures();
  const notifs = useNotifications(!!user && !isInvitado);
  const { mode, toggleColorMode } = useContext(ColorModeContext);

  // Atajo Ctrl+K para abrir búsqueda global — no aplica a invitado, que no
  // tiene acceso a pacientes (la búsqueda pega a /patients, bloqueado por
  // el guard del backend para ese rol).
  useEffect(() => {
    if (isInvitado) return;
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isInvitado]);

  useEffect(() => {
    if (!user) return;
    const today = new Date();
    const start = new Date(today); start.setHours(0, 0, 0, 0);
    const end = new Date(today); end.setHours(23, 59, 59, 999);
    getAppointments({
      start_date: start.toISOString(),
      end_date: end.toISOString(),
      limit: 1,
    }).then(r => setTodayCount(r.data?.total ?? 0)).catch(() => {});
  }, [user]);

  // Warning 5 minutos antes de que expire la sesión
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (!payload.exp) return;
      const msUntilWarning = payload.exp * 1000 - Date.now() - 5 * 60 * 1000;
      if (msUntilWarning <= 0) return;
      const t = setTimeout(() => setSessionWarning(true), msUntilWarning);
      return () => clearTimeout(t);
    } catch { /* token malformado, ignorar */ }
  }, [user]);

  if (!user) return <>{children}</>;

  const openUserMenu = (e) => setUserMenuAnchor(e.currentTarget);
  const closeUserMenu = () => setUserMenuAnchor(null);

  const navItems = buildNavItems(isAdmin, isMedico, isRecepcionista, isInvitado, todayCount, features);
  // Desktop AppBar: only regular items (no section separators, no drawerOnly items)
  const desktopNavItems = navItems.filter(i => !i.type && !i.drawerOnly);
  // Management links shown in the desktop account dropdown for admin
  const adminMenuItems = isAdmin ? navItems.filter(i => i.drawerOnly) : [];
  // Role-appropriate home (matches App.js getDefaultRoute)
  const homeRoute = isAdmin ? '/metrics' : (isRecepcionista || isMedico) ? '/dashboard-recepcionista' : isInvitado ? '/algoritmos' : '/agenda';

  return (
    <>
      <AppBar position="sticky" elevation={0} sx={{ pt: 'env(safe-area-inset-top)' }}>
        <Toolbar sx={{ gap: 0.5, minHeight: { xs: 56, md: 58 }, px: { xs: 2, md: 3 } }}>
          {/* Brand — links to role home */}
          <Box
            component={RouterLink}
            to={homeRoute}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              textDecoration: 'none',
              color: 'white',
              mr: 2,
              flexShrink: 0,
            }}
          >
            <BrandMark size={22} variant="bare" />
            <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', letterSpacing: '-0.01em' }}>
              Vita<span style={{ opacity: 0.75 }}>Notas</span>
            </Typography>
          </Box>

          {/* Desktop nav links — excludes drawerOnly items */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flex: 1 }}>
              {desktopNavItems.map(item => {
                const active = location.pathname === item.path;
                return (
                  <Button
                    key={item.path}
                    component={RouterLink}
                    to={item.path}
                    startIcon={item.icon}
                    sx={{
                      color: 'white',
                      fontWeight: active ? 700 : 400,
                      fontSize: '0.83rem',
                      px: 1.25,
                      py: 0.625,
                      minWidth: 'auto',
                      bgcolor: active ? 'rgba(255,255,255,0.18)' : 'transparent',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' },
                      whiteSpace: 'nowrap',
                      borderRadius: 1.5,
                    }}
                  >
                    {item.label}
                  </Button>
                );
              })}
            </Box>
          )}

          <Box sx={{ flex: 1 }} />

          {/* Modo oscuro */}
          <Tooltip title={mode === 'dark' ? 'Modo claro' : 'Modo oscuro'}>
            <IconButton size="small" onClick={toggleColorMode} sx={{ color: 'white', mr: 0.25 }}>
              {mode === 'dark'
                ? <LightModeOutlinedIcon sx={{ fontSize: 20 }} />
                : <DarkModeOutlinedIcon sx={{ fontSize: 20 }} />}
            </IconButton>
          </Tooltip>

          {/* Notificaciones y búsqueda global de pacientes — ninguna de las
              dos aplica a invitado, que no tiene acceso a pacientes/turnos. */}
          {!isInvitado && (
            <>
              <Tooltip title="Notificaciones">
                <IconButton
                  size="small"
                  onClick={e => setNotifAnchor(e.currentTarget)}
                  sx={{ color: 'white', mr: 0.5 }}
                >
                  <Badge badgeContent={notifs.total || 0} color="error" max={99}
                    sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 14, minWidth: 14 } }}>
                    <NotificationsNoneIcon sx={{ fontSize: 20 }} />
                  </Badge>
                </IconButton>
              </Tooltip>

              {/* En mobile el mismo buscador ya vive en el drawer (abajo) —
                  repetirlo acá con el texto completo + "Ctrl+K" (que no
                  significa nada sin teclado) solo le roba espacio al resto
                  del AppBar. */}
              {!isMobile && (
                <Button
                  onClick={() => setSearchOpen(true)}
                  startIcon={<SearchIcon sx={{ fontSize: '16px !important' }} />}
                  sx={{
                    color: 'rgba(255,255,255,0.75)',
                    fontSize: '0.78rem',
                    px: 1.5,
                    py: 0.5,
                    mr: 1,
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: 1.5,
                    bgcolor: 'rgba(255,255,255,0.08)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.15)', color: 'white' },
                    minWidth: 140,
                    justifyContent: 'flex-start',
                    gap: 1,
                  }}
                >
                  Buscar paciente
                  <Typography component="span" sx={{ ml: 'auto', fontSize: '0.65rem', opacity: 0.6, fontFamily: 'monospace' }}>
                    Ctrl+K
                  </Typography>
                </Button>
              )}
            </>
          )}

          {/* Desktop user area */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
              {isSuperadmin && !actingAsSupport && <ClinicPicker />}
              {role && (
                <Chip
                  label={ROLE_LABELS[role] ?? role}
                  color={ROLE_COLORS[role] ?? 'default'}
                  size="small"
                  sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }}
                />
              )}
              <Tooltip title="Cuenta">
                <Box
                  onClick={openUserMenu}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer',
                    px: 1, py: 0.5, borderRadius: 1.5,
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
                >
                  <Avatar sx={{ width: 28, height: 28, bgcolor: 'rgba(255,255,255,0.2)', fontSize: '0.8rem', color: 'white' }}>
                    {user?.username?.[0]?.toUpperCase()}
                  </Avatar>
                  <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.82rem', fontWeight: 500 }}>
                    {user?.username}
                  </Typography>
                </Box>
              </Tooltip>
            </Box>
          )}

          {/* Mobile hamburger */}
          {isMobile && (
            <IconButton color="inherit" onClick={() => setDrawerOpen(true)} edge="end" size="small">
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {actingAsSupport && <SupportBanner clinicName={user?.clinic_name} />}

      {/* Mobile drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 260, bgcolor: 'background.paper' } }}
      >
        {/* User header */}
        <Box sx={{ px: 2, py: 2, bgcolor: 'primary.dark' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ width: 36, height: 36, bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontSize: '1rem' }}>
              {user?.username?.[0]?.toUpperCase()}
            </Avatar>
            <Box>
              <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '0.9rem' }}>{user?.username}</Typography>
              <Chip
                label={ROLE_LABELS[role] ?? role}
                color={ROLE_COLORS[role] ?? 'default'}
                size="small"
                sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, mt: 0.25 }}
              />
            </Box>
          </Box>
        </Box>

        <List sx={{ px: 1, py: 1.5, flex: 1 }}>
          {!isInvitado && (
            <ListItem disablePadding sx={{ mb: 1 }}>
              <ListItemButton onClick={() => { setDrawerOpen(false); setSearchOpen(true); }}
                sx={{ borderRadius: 1.5, bgcolor: 'grey.100' }}>
                <ListItemIcon sx={{ minWidth: 36, color: 'text.secondary' }}>
                  <SearchIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Buscar paciente" primaryTypographyProps={{ fontSize: '0.9rem' }} />
                <Typography variant="caption" sx={{ opacity: 0.5, fontFamily: 'monospace' }}>Ctrl+K</Typography>
              </ListItemButton>
            </ListItem>
          )}
          {navItems.map((item, idx) => {
            // Section separator with label
            if (item.type === 'section') {
              return (
                <Box key={`section-${idx}`} sx={{ mt: 1, mb: 0.5 }}>
                  <Divider />
                  <Typography variant="caption" color="text.disabled"
                    sx={{ px: 1.5, pt: 1, pb: 0.25, display: 'block', textTransform: 'uppercase', letterSpacing: 0.8, fontSize: '0.65rem', fontWeight: 700 }}>
                    {item.label}
                  </Typography>
                </Box>
              );
            }
            const active = location.pathname === item.path;
            return (
              <ListItem key={item.path} disablePadding sx={{ mb: 0.25 }}>
                <ListItemButton
                  component={RouterLink}
                  to={item.path}
                  onClick={() => setDrawerOpen(false)}
                  selected={active}
                  sx={{
                    borderRadius: 1.5,
                    '&.Mui-selected': {
                      bgcolor: 'rgba(21,101,192,0.1)',
                      color: 'primary.main',
                      '&:hover': { bgcolor: 'rgba(21,101,192,0.15)' },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36, color: active ? 'primary.main' : 'text.secondary' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: active ? 700 : 400 }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>

        <Divider />
        <List sx={{ px: 1, py: 0.5 }}>
          {isMedico && (
            <ListItem disablePadding>
              <ListItemButton onClick={() => { setDrawerOpen(false); setDoctorProfileOpen(true); }} sx={{ borderRadius: 1.5 }}>
                <ListItemIcon sx={{ minWidth: 36, color: 'text.secondary' }}>
                  <BadgeIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Mi perfil médico" primaryTypographyProps={{ fontSize: '0.9rem' }} />
              </ListItemButton>
            </ListItem>
          )}
          <ListItem disablePadding>
            <ListItemButton onClick={() => { setDrawerOpen(false); setPwDialogOpen(true); }} sx={{ borderRadius: 1.5 }}>
              <ListItemIcon sx={{ minWidth: 36, color: 'text.secondary' }}>
                <LockOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Cambiar contraseña" primaryTypographyProps={{ fontSize: '0.9rem' }} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => { setDrawerOpen(false); setInstalarGuiaOpen(true); }} sx={{ borderRadius: 1.5 }}>
              <ListItemIcon sx={{ minWidth: 36, color: 'text.secondary' }}>
                <SmartphoneIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Instalar la app en el celular" primaryTypographyProps={{ fontSize: '0.9rem' }} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => { setDrawerOpen(false); logout(); }} sx={{ borderRadius: 1.5 }}>
              <ListItemIcon sx={{ minWidth: 36, color: 'error.main' }}>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Cerrar sesión" primaryTypographyProps={{ fontSize: '0.9rem', color: 'error.main', fontWeight: 500 }} />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>

      {/* Notifications popover */}
      <Popover
        open={Boolean(notifAnchor)}
        anchorEl={notifAnchor}
        onClose={() => setNotifAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { mt: 0.5, minWidth: 240, maxWidth: 300 } }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2">Notificaciones</Typography>
        </Box>
        {notifs.total === 0 ? (
          <Box sx={{ px: 2, py: 2 }}>
            <Typography variant="body2" color="text.secondary">Sin notificaciones pendientes</Typography>
          </Box>
        ) : (
          <List dense disablePadding sx={{ py: 0.5 }}>
            {/* Cada línea navega directo a donde se resuelve — antes eran
                puro texto informativo, sin ningún atajo, así que había que
                ir a buscar manualmente lo que la campana ya sabía. La sala
                de espera vive embebida en el dashboard del rol (no tiene
                ruta propia), por eso navega al home de admin/recep/médico
                en vez de a una URL fija. */}
            {notifs.waiting_room > 0 && (
              <ListItemButton
                sx={{ gap: 1.5, px: 2, py: 0.75 }}
                onClick={() => {
                  setNotifAnchor(null);
                  navigate('/dashboard-recepcionista');
                }}
              >
                <HourglassEmptyIcon sx={{ fontSize: 18, color: 'warning.main', flexShrink: 0 }} />
                <Typography variant="body2">
                  <strong>{notifs.waiting_room}</strong> paciente{notifs.waiting_room !== 1 ? 's' : ''} en sala de espera
                </Typography>
              </ListItemButton>
            )}
            {notifs.pending_today > 0 && (
              <ListItemButton
                sx={{ gap: 1.5, px: 2, py: 0.75 }}
                onClick={() => {
                  setNotifAnchor(null);
                  navigate('/turnos', { state: { initialStateFilter: { state: 'pendiente', startDate: '2000-01-01' } } });
                }}
              >
                <PendingActionsIcon sx={{ fontSize: 18, color: 'error.main', flexShrink: 0 }} />
                <Typography variant="body2">
                  <strong>{notifs.pending_today}</strong> turno{notifs.pending_today !== 1 ? 's' : ''} sin confirmar hoy
                </Typography>
              </ListItemButton>
            )}
            {notifs.whatsapp_due > 0 && (
              <ListItemButton
                sx={{ gap: 1.5, px: 2, py: 0.75 }}
                onClick={() => { setNotifAnchor(null); navigate('/agenda'); }}
              >
                <WhatsAppIcon sx={{ fontSize: 18, color: '#25D366', flexShrink: 0 }} />
                <Typography variant="body2">
                  <strong>{notifs.whatsapp_due}</strong> recordatorio{notifs.whatsapp_due !== 1 ? 's' : ''} de WhatsApp para mañana
                </Typography>
              </ListItemButton>
            )}
          </List>
        )}
      </Popover>

      {/* User account menu (desktop) */}
      <Menu anchorEl={userMenuAnchor} open={Boolean(userMenuAnchor)} onClose={closeUserMenu}
        PaperProps={{ sx: { minWidth: 200, mt: 0.5 } }}>
        <MenuItem disabled sx={{ fontSize: '0.8rem', opacity: 1 }}>
          <Typography variant="caption" color="text.secondary">{user?.username}</Typography>
        </MenuItem>
        <Divider />
        {isMedico && (
          <MenuItem onClick={() => { closeUserMenu(); setDoctorProfileOpen(true); }}>
            <BadgeIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
            Mi perfil médico
          </MenuItem>
        )}
        {/* Admin management links — one level deeper than daily-work nav.
            MUI's Menu requires a flat array of children (no Fragment) or it
            warns in the console and can misrender keyboard navigation. */}
        {adminMenuItems.length > 0 && [
          <Typography key="admin-label" variant="caption" color="text.disabled"
            sx={{ px: 2, pt: 1, pb: 0.5, display: 'block', textTransform: 'uppercase', letterSpacing: 0.8, fontSize: '0.65rem', fontWeight: 700 }}>
            Administración
          </Typography>,
          ...adminMenuItems.map(item => (
            <MenuItem key={item.path} component={RouterLink} to={item.path} onClick={closeUserMenu}
              selected={location.pathname === item.path}
              sx={{ fontSize: '0.88rem', gap: 1 }}>
              <Box sx={{ color: 'text.secondary', display: 'flex' }}>{item.icon}</Box>
              {item.label}
            </MenuItem>
          )),
          <Divider key="admin-divider" />,
        ]}
        <MenuItem onClick={() => { closeUserMenu(); setPwDialogOpen(true); }}>
          <LockOutlinedIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
          Cambiar contraseña
        </MenuItem>
        <MenuItem onClick={() => { closeUserMenu(); logout(); }} sx={{ color: 'error.main' }}>
          <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
          Cerrar sesión
        </MenuItem>
      </Menu>

      {/* Change password dialog */}
      <ChangePasswordDialog open={pwDialogOpen} onClose={() => setPwDialogOpen(false)} />
      {isMedico && <DoctorProfileDialog open={doctorProfileOpen} onClose={() => setDoctorProfileOpen(false)} />}
      <GuiaInstalacionDialog open={instalarGuiaOpen} onClose={() => setInstalarGuiaOpen(false)} />

      <Snackbar
        open={sessionWarning}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={() => setSessionWarning(false)}
      >
        <Alert
          severity="warning"
          variant="filled"
          action={
            <Button color="inherit" size="small" onClick={() => { setSessionWarning(false); logout(); }}>
              Cerrar sesión
            </Button>
          }
          sx={{ alignItems: 'center' }}
        >
          Tu sesión vence en 5 minutos. Guardá tu trabajo.
        </Alert>
      </Snackbar>

      {/* Búsqueda global */}
      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />

      {/* Main content. overflow-x:hidden acá — una tabla ancha en una página
          hija (ej. "Agenda del día" en RecepcionistDashboard.js) podía
          empujar el ancho de TODO el documento en mobile en vez de quedar
          contenida por su propio overflow-x:auto interno, bloqueando el
          swipe horizontal de esa tabla (el gesto se "perdía" en el scroll
          de la página, que no se movía porque no había nada más ancho que
          el viewport a simple vista). Reportado con captura 2026-11-02,
          persistía después de poner overflow:hidden solo en el Paper de
          esa página — la causa real estaba acá, un nivel más arriba,
          compartido por todas las páginas. */}
      <Box component="main" sx={{ minHeight: 'calc(100vh - 58px)', bgcolor: 'background.default', overflowX: 'hidden' }}>
        <Box sx={{
          maxWidth: 1400, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 },
          pb: { xs: 'calc(16px + env(safe-area-inset-bottom))', sm: 3 },
        }}>
          {children}
        </Box>
      </Box>
    </>
  );
};

export default Layout;
